// @ts-nocheck
/**
 * 装備強化テーブル
 *
 * 装備のレベルアップと覚醒に必要なコストと効果を定義
 *
 * レベル: 1-10（ステータス増加）
 * 覚醒: ★0-5（特殊効果追加）
 */

/**
 * レベルアップ基礎コスト（ティア別）
 * 実際のコスト = baseCost × currentLevel
 *
 * 例: rare装備をLv.3→4にする場合
 * 魔力の核: 15 × 3 = 45
 * SR破片: 3 × 3 = 9
 */
export const LEVEL_COSTS = {
  common: [
    { type: '魔力の核', count: 10 },
    { type: 'R破片', count: 2 },
  ],
  rare: [
    { type: '魔力の核', count: 15 },
    { type: 'SR破片', count: 3 },
  ],
  epic: [
    { type: '魔力の核', count: 25 },
    { type: 'HR破片', count: 4 },
    { type: '秘宝の欠片', count: 1 },
  ],
  legendary: [
    { type: '魔力の核', count: 50 },
    { type: 'UR破片', count: 5 },
    { type: '秘宝の欠片', count: 3 },
    { type: '原初の結晶', count: 1 },
  ],
};

/**
 * レベルごとのステータス倍率
 * 基礎ステータス × multiplier = 実際のステータス
 */
export const LEVEL_MULTIPLIERS = {
  1: 1.0,
  2: 1.1,
  3: 1.2,
  4: 1.3,
  5: 1.45,
  6: 1.6,
  7: 1.8,
  8: 2.0,
  9: 2.25,
  10: 2.5,
};

/**
 * 覚醒コスト（覚醒段階別）
 * ★1→★5の各段階で必要な素材
 */
export const AWAKENING_COSTS = {
  1: [
    { type: '魔力の核', count: 100 },
    { type: 'SR破片', count: 20 },
    { type: '秘宝の欠片', count: 5 },
  ],
  2: [
    { type: '魔力の核', count: 200 },
    { type: 'HR破片', count: 30 },
    { type: '秘宝の欠片', count: 10 },
  ],
  3: [
    { type: '魔力の核', count: 350 },
    { type: 'UR破片', count: 25 },
    { type: '秘宝の欠片', count: 20 },
    { type: '記憶の結晶', count: 3 },
  ],
  4: [
    { type: '魔力の核', count: 500 },
    { type: 'SEC破片', count: 30 },
    { type: '秘宝の欠片', count: 35 },
    { type: '記憶の結晶', count: 8 },
    { type: '原初の結晶', count: 2 },
  ],
  5: [
    { type: '魔力の核', count: 800 },
    { type: 'GR破片', count: 20 },
    { type: '秘宝の欠片', count: 50 },
    { type: '記憶の結晶', count: 15 },
    { type: '原初の結晶', count: 5 },
  ],
};

/**
 * 覚醒ボーナス効果
 * 各覚醒段階で追加される効果
 */
export const AWAKENING_BONUSES = {
  1: {
    statBonus: 0.05,  // 全ステータス+5%
    description: '全ステータス+5%',
    effects: [],
  },
  2: {
    statBonus: 0.1,   // 累計+10%
    description: '全ステータス+10%',
    effects: [
      { type: 'minor_regeneration', percent: 0.01, description: '毎ターンHP1%回復' },
    ],
  },
  3: {
    statBonus: 0.15,  // 累計+15%
    description: '全ステータス+15%',
    effects: [
      { type: 'skill_cooldown_reduction', percent: 0.1, description: 'スキルクールダウン-10%' },
    ],
  },
  4: {
    statBonus: 0.2,   // 累計+20%
    description: '全ステータス+20%',
    effects: [
      { type: 'critical_rate_bonus', value: 5, description: 'クリティカル率+5%' },
    ],
  },
  5: {
    statBonus: 0.3,   // 累計+30%
    description: '全ステータス+30%',
    effects: [
      { type: 'ultimate_awakening', value: true, description: '装備固有の究極効果を解放' },
    ],
  },
};

/**
 * ティア別の究極効果（★5覚醒時）
 * 装備のティアに応じた追加効果
 */
export const ULTIMATE_AWAKENING_EFFECTS = {
  common: {
    type: 'stat_boost_all',
    value: 0.1,
    description: '全ステータス+10%（追加）',
  },
  rare: {
    type: 'set_effect_boost',
    value: 0.5,
    description: 'セット効果の発動条件-1',
  },
  epic: {
    type: 'skill_enhancement',
    value: true,
    description: '装備効果が1.5倍に強化',
  },
  legendary: {
    type: 'transcendence',
    value: true,
    description: '超越効果（装備固有の伝説効果を解放）',
  },
};

/**
 * 成功率テーブル（オプション：強化失敗システム用）
 * 現在は100%成功だが、将来的に失敗システムを導入可能
 */
export const SUCCESS_RATES = {
  level: {
    1: 1.0,
    2: 1.0,
    3: 1.0,
    4: 1.0,
    5: 1.0,
    6: 1.0,
    7: 1.0,
    8: 1.0,
    9: 1.0,
    10: 1.0,
  },
  awakening: {
    1: 1.0,
    2: 1.0,
    3: 1.0,
    4: 1.0,
    5: 1.0,
  },
};

/**
 * 強化テーブル統合オブジェクト
 */
export const ENHANCEMENT_TABLE = {
  levelCosts: LEVEL_COSTS,
  levelMultipliers: LEVEL_MULTIPLIERS,
  awakeningCosts: AWAKENING_COSTS,
  awakeningBonuses: AWAKENING_BONUSES,
  ultimateAwakeningEffects: ULTIMATE_AWAKENING_EFFECTS,
  successRates: SUCCESS_RATES,
  maxLevel: 10,
  maxAwakening: 5,
};

/**
 * レベルアップコストを計算
 *
 * @param {string} tier - 装備ティア
 * @param {number} currentLevel - 現在のレベル
 * @returns {Object[]} 必要素材の配列
 */
export function calculateLevelUpCost(tier, currentLevel) {
  const baseCosts = LEVEL_COSTS[tier];
  if (!baseCosts) return [];

  return baseCosts.map(cost => ({
    type: cost.type,
    count: cost.count * currentLevel,
  }));
}

/**
 * 覚醒コストを取得
 *
 * @param {number} targetAwakening - 目標覚醒段階 (1-5)
 * @returns {Object[]} 必要素材の配列
 */
export function getAwakeningCost(targetAwakening) {
  return AWAKENING_COSTS[targetAwakening] || [];
}

/**
 * レベルによるステータス倍率を取得
 *
 * @param {number} level - 装備レベル
 * @returns {number} ステータス倍率
 */
export function getLevelMultiplier(level) {
  return LEVEL_MULTIPLIERS[level] || 1.0;
}

/**
 * 覚醒によるステータスボーナスを取得
 *
 * @param {number} awakening - 覚醒段階
 * @returns {number} ステータスボーナス率
 */
export function getAwakeningStatBonus(awakening) {
  return AWAKENING_BONUSES[awakening]?.statBonus || 0;
}

/**
 * 覚醒効果を取得
 *
 * @param {number} awakening - 覚醒段階
 * @returns {Object[]} 効果の配列
 */
export function getAwakeningEffects(awakening) {
  const effects = [];

  // 指定覚醒段階までの全効果を収集
  for (let i = 1; i <= awakening; i++) {
    const bonus = AWAKENING_BONUSES[i];
    if (bonus && bonus.effects) {
      effects.push(...bonus.effects);
    }
  }

  return effects;
}

/**
 * 合計ステータス倍率を計算（レベル + 覚醒）
 *
 * @param {number} level - 装備レベル
 * @param {number} awakening - 覚醒段階
 * @returns {number} 合計ステータス倍率
 */
export function getTotalStatMultiplier(level, awakening) {
  const levelMult = getLevelMultiplier(level);
  const awakeningBonus = getAwakeningStatBonus(awakening);
  return levelMult * (1 + awakeningBonus);
}

/**
 * 強化後のステータスを計算
 *
 * @param {Object} baseStats - 基礎ステータス
 * @param {number} level - 装備レベル
 * @param {number} awakening - 覚醒段階
 * @returns {Object} 強化後のステータス
 */
export function calculateEnhancedStats(baseStats, level, awakening) {
  const multiplier = getTotalStatMultiplier(level, awakening);
  const enhanced = {};

  for (const [stat, value] of Object.entries(baseStats)) {
    enhanced[stat] = Math.floor(value * multiplier);
  }

  return enhanced;
}

/**
 * Lv.1からLv.maxまでの累計コストを計算
 *
 * @param {string} tier - 装備ティア
 * @param {number} targetLevel - 目標レベル
 * @returns {Object} 素材タイプをキーとした合計数
 */
export function calculateTotalLevelCost(tier, targetLevel) {
  const totals = {};

  for (let level = 1; level < targetLevel; level++) {
    const costs = calculateLevelUpCost(tier, level);
    for (const cost of costs) {
      totals[cost.type] = (totals[cost.type] || 0) + cost.count;
    }
  }

  return totals;
}

/**
 * ★0から★maxまでの累計コストを計算
 *
 * @param {number} targetAwakening - 目標覚醒段階
 * @returns {Object} 素材タイプをキーとした合計数
 */
export function calculateTotalAwakeningCost(targetAwakening) {
  const totals = {};

  for (let awakening = 1; awakening <= targetAwakening; awakening++) {
    const costs = getAwakeningCost(awakening);
    for (const cost of costs) {
      totals[cost.type] = (totals[cost.type] || 0) + cost.count;
    }
  }

  return totals;
}

/**
 * 強化可能かチェック
 *
 * @param {Object} equipment - 装備
 * @param {string} enhanceType - 'level' | 'awakening'
 * @returns {{ canEnhance: boolean, reason: string|null }}
 */
export function canEnhance(equipment, enhanceType) {
  if (enhanceType === 'level') {
    if (equipment.level >= ENHANCEMENT_TABLE.maxLevel) {
      return { canEnhance: false, reason: '最大レベルに達しています' };
    }
    return { canEnhance: true, reason: null };
  }

  if (enhanceType === 'awakening') {
    if (equipment.awakening >= ENHANCEMENT_TABLE.maxAwakening) {
      return { canEnhance: false, reason: '最大覚醒に達しています' };
    }
    // 覚醒にはレベル5以上が必要（オプション）
    if (equipment.level < 5) {
      return { canEnhance: false, reason: '覚醒にはLv.5以上が必要です' };
    }
    return { canEnhance: true, reason: null };
  }

  return { canEnhance: false, reason: '無効な強化タイプです' };
}

/**
 * 強化の効果をプレビュー
 *
 * @param {Object} equipment - 装備
 * @param {string} enhanceType - 'level' | 'awakening'
 * @returns {Object} プレビュー情報
 */
export function previewEnhancement(equipment, enhanceType) {
  if (enhanceType === 'level') {
    const newLevel = equipment.level + 1;
    const newMultiplier = getTotalStatMultiplier(newLevel, equipment.awakening);
    const currentMultiplier = getTotalStatMultiplier(equipment.level, equipment.awakening);

    return {
      type: 'level',
      current: equipment.level,
      next: newLevel,
      statChange: newMultiplier / currentMultiplier,
      cost: calculateLevelUpCost(equipment.tier, equipment.level),
    };
  }

  if (enhanceType === 'awakening') {
    const newAwakening = equipment.awakening + 1;
    const bonus = AWAKENING_BONUSES[newAwakening];

    return {
      type: 'awakening',
      current: equipment.awakening,
      next: newAwakening,
      statBonus: bonus?.statBonus || 0,
      newEffects: bonus?.effects || [],
      description: bonus?.description || '',
      cost: getAwakeningCost(newAwakening),
    };
  }

  return null;
}

export default {
  ENHANCEMENT_TABLE,
  LEVEL_COSTS,
  LEVEL_MULTIPLIERS,
  AWAKENING_COSTS,
  AWAKENING_BONUSES,
  ULTIMATE_AWAKENING_EFFECTS,
  SUCCESS_RATES,
  calculateLevelUpCost,
  getAwakeningCost,
  getLevelMultiplier,
  getAwakeningStatBonus,
  getAwakeningEffects,
  getTotalStatMultiplier,
  calculateEnhancedStats,
  calculateTotalLevelCost,
  calculateTotalAwakeningCost,
  canEnhance,
  previewEnhancement,
};
